import { createQueryKeys } from '@lukemorales/query-key-factory';

import { get } from '@/api/api';
import type {
  FanguardApiContractsClientAssetModel,
  FanguardApiContractsClientClientResponseDetailModel,
  FanguardApiContractsClientConnectionRequestModelMerged,
  FanguardCoreManagersCredentialingCredentialArea,
  FanguardCoreManagersCredentialingCredentialingPrinters,
} from 'src/api/generated';
import { buildQueryParams } from 'src/hooks/api/helpers';
import type { AssetType } from 'src/pages/ClientConfiguration/Assets/utils';

export const CLIENTS_URI = 'clients';
export const FR_NETWORKS_URI = 'frNetworks';

export const frNetworksQueries = {
  all: ['frNetworks'] as const,
};

export const clientsQueries = createQueryKeys(CLIENTS_URI, {
  all: null,
  credentialingAreas: (connectionId: string) => ({
    queryKey: [connectionId, 'credentialingAreas'] as const,
    queryFn: async () => {
      const params = { connectionId };
      const queryString = buildQueryParams(params);
      const response = await get<FanguardCoreManagersCredentialingCredentialArea[]>({
        uri: `${CLIENTS_URI}/credentialing-areas?${queryString}`,
      });
      return response.data;
    },
  }),
  detail: (id: string) => ({
    queryKey: [id],
    queryFn: async () => {
      const response = await get<FanguardApiContractsClientClientResponseDetailModel>({
        uri: `${CLIENTS_URI}/${id}`,
      });
      return response.data;
    },
  }),
  printers: (connectionId: string) => ({
    queryKey: [connectionId, 'printers'] as const,
    queryFn: async () => {
      const response = await get<FanguardCoreManagersCredentialingCredentialingPrinters>({
        uri: connectionId ? `${CLIENTS_URI}/printers?connectionId=${connectionId}` : `${CLIENTS_URI}/printers`,
      });
      return response.data;
    },
  }),
  // Typed keys extend [id, 'assets'], so invalidating the untyped key after an upload or delete refreshes them too.
  assets: (id: string, type?: AssetType) => ({
    queryKey: type ? [id, 'assets', type] : [id, 'assets'],
    queryFn: async () => {
      const response = await get<FanguardApiContractsClientAssetModel[]>({
        uri: type ? `${CLIENTS_URI}/${id}/asset?type=${type}` : `${CLIENTS_URI}/${id}/asset`,
      });
      return response.data;
    },
  }),
  connection: (clientId: string, connectionId: string) => ({
    queryKey: [clientId, connectionId],
    queryFn: async () => {
      const response = await get<FanguardApiContractsClientConnectionRequestModelMerged>({
        uri: `${CLIENTS_URI}/${clientId}/connection/${connectionId}`,
      });
      return response.data;
    },
  }),
  connections: (clientId: string) => ({
    queryKey: [clientId, 'connections'],
    queryFn: async () => {
      const response = await get<FanguardApiContractsClientConnectionRequestModelMerged[]>({
        uri: `${CLIENTS_URI}/${clientId}/connections`,
      });
      return response.data;
    },
  }),
  reportCategories: (clientId: string) => ({
    queryKey: [clientId, 'report-categories'],
    queryFn: async () => {
      const response = await get<string[]>({
        uri: `${CLIENTS_URI}/${clientId}/report-categories`,
      });
      return response.data;
    },
  }),
});
